import grooveAPI from 'api/groove'
import { BEGIN, COMMIT, REVERT } from 'redux-optimist'
import { v4 as uuidV4 } from 'uuid'
import { oauthTokenSelector } from 'selectors/app'
import {
  doGraphqlRequest,
  doApiReadRequest,
  doRequest,
  doApiPutRequest,
} from 'ducks/requests/operations'
import { looselyEquals, omit } from 'util/objects'
import {
  agentNotificationPreferences as agentNotificationPreferencesEntity,
  agentPreferences as agentPreferencesEntity,
} from 'ducks/entities/schema'
import {
  UPDATE_CURRENT_USER,
  UPDATE_USER_PREFERENCES_REQUEST,
  UPDATE_USER_PREFERENCES_SUCCESS,
  UPDATE_USER_PREFERENCES_FAIL,
  RESEND_CONFIRMATION_TOKEN,
  UPLOAD_CURRENT_USER_AVATAR,
  FETCH_NOTIFICATION_PREFERENCES,
  UPDATE_NOTIFICATION_PREFERENCES,
  FETCH_PREFERENCES,
  UPDATE_PREFERENCES,
} from '../types'
import {
  updateCurrentUserSignupMutation,
  confirmCurrentUserMutation,
  updateCurrentUserMutation,
  changePasswordMutation,
} from '../api'
import { selectCurrentUser } from '../selectors'

export const doUploadAvatar = file => dispatch => {
  const body = new FormData()
  body.append('current_user[avatar]', file)

  return dispatch(
    doRequest(UPLOAD_CURRENT_USER_AVATAR, async ({ getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)
      const { json } = await grooveAPI.send(
        'post',
        token,
        'v1/me/avatar',
        null,
        body,
        {},
        { skipContentTypeHeader: true }
      )
      return json
    })
  )
}

export const doUpdateCurrentUserForOnboarding = (
  fields,
  options = {}
) => async dispatch => {
  return dispatch(
    doGraphqlRequest(
      UPDATE_CURRENT_USER,
      updateCurrentUserSignupMutation,
      { input: { ...fields } },
      {
        throwOnError: true,
        ...options,
      }
    )
  )
}

export const doChangePassword = (fields, options = {}) => async dispatch => {
  const { currentPassword, password, passwordConfirmation } = fields
  return dispatch(
    doGraphqlRequest(
      UPDATE_CURRENT_USER,
      changePasswordMutation,
      { input: { currentPassword, password, passwordConfirmation } },
      options
    )
  )
}

export const doUpdateCurrentUser = (fields, options = {}) => async (
  dispatch,
  getState
) => {
  const state = getState()
  const currentUser = selectCurrentUser(state)
  const avatarField = fields.avatar
  // NOTE (jscheel): If the field is and empty string, we still call
  // doUploadAvatar to let it remove the user's avatar. However, we do not try
  // to upload the avatar otherwise. Unfortunately, we are all over the map with
  // avatar urls right now, so this tries to be smart about updating the avatar.
  if (avatarField || avatarField === '') {
    let currentAvatar = currentUser.avatar_url || ''
    if (currentAvatar.match('missing.png')) {
      currentAvatar = ''
    }
    if (avatarField !== currentAvatar) {
      await dispatch(doUploadAvatar(avatarField))
    }
  }

  // HACK (jscheel): This sucks to do these in series, but until we work out our
  // preference and current user models, and how gql should do nested updates, this
  // is the fastest way to take care of these changes. Definitely tech debt.
  if (
    fields.preferences &&
    !looselyEquals(fields.preferences, currentUser.preferences)
  ) {
    await dispatch(doUpdateUserPreferences(fields.preferences))
  }

  return dispatch(
    doGraphqlRequest(
      UPDATE_CURRENT_USER,
      updateCurrentUserMutation,
      { input: { ...omit(['avatar', 'preferences'], fields) } },
      options
    )
  )
}

export function doUpdateUserPreferences(prefs) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const tid = uuidV4()
    const serializedData = JSON.stringify({ agent: prefs })
    dispatch({
      type: UPDATE_USER_PREFERENCES_REQUEST,
      payload: { preferences: prefs },
      optimist: { type: BEGIN, id: tid },
    })
    return grooveAPI
      .put(token, `/v1/preferences`, null, serializedData)
      .then(() => {
        dispatch({
          type: UPDATE_USER_PREFERENCES_SUCCESS,
          optimist: { type: COMMIT, id: tid },
        })
      })
      .catch(error => {
        dispatch({
          type: UPDATE_USER_PREFERENCES_FAIL,
          payload: { error },
          optimist: { type: REVERT, id: tid },
        })
      })
  }
}

export const doConfirmCurrentUser = confirmationToken => {
  return dispatch => {
    return dispatch(
      doGraphqlRequest(
        UPDATE_CURRENT_USER,
        confirmCurrentUserMutation,
        { input: { confirmationToken } },
        { throwOnError: true }
      )
    )
  }
}

export const doResendConfirmationToken = () => {
  return dispatch => {
    return dispatch(
      doApiReadRequest(
        RESEND_CONFIRMATION_TOKEN,
        'v1/me/resend_confirmation_token'
      )
    )
  }
}

export const doFetchNotificationPreferences = (options = {}) => dispatch => {
  return dispatch(
    doApiReadRequest(
      FETCH_NOTIFICATION_PREFERENCES,
      '/api/settings/notifications',
      {},
      {
        normalizationSchema: agentNotificationPreferencesEntity,
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: false,
            },
            failed: {
              content: 'Loading preferences failed',
              onClickAction: () =>
                dispatch(doFetchNotificationPreferences(options)),
            },
          },
        },
        ...options,
      }
    )
  )
}

export const doUpdateNotificationPreferences = (
  agentNotificationPreferences,
  options = {}
) => dispatch => {
  return dispatch(
    doApiPutRequest(
      UPDATE_NOTIFICATION_PREFERENCES,
      '/api/settings/notifications',
      {
        notification: agentNotificationPreferences,
      },
      {
        normalizationSchema: agentNotificationPreferencesEntity,
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: true,
              content: 'Preferences changes saved',
            },
            failed: {
              content: 'Preferences changes failed',
              onClickAction: () =>
                dispatch(
                  doUpdateNotificationPreferences(
                    agentNotificationPreferences,
                    options
                  )
                ),
            },
          },
        },
        ...options,
      }
    )
  )
}

export const doFetchPreferences = (options = {}) => dispatch => {
  return dispatch(
    doApiReadRequest(
      FETCH_PREFERENCES,
      '/api/settings/preferences',
      {},
      {
        normalizationSchema: agentPreferencesEntity,
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: false,
            },
            failed: {
              content: 'Loading preferences failed',
              onClickAction: () => dispatch(doFetchPreferences(options)),
            },
          },
        },
        ...options,
      }
    )
  )
}

export const doUpdatePreferences = (
  agentPreference,
  options = {}
) => dispatch => {
  return dispatch(
    doApiPutRequest(
      UPDATE_PREFERENCES,
      '/api/settings/preferences',
      {
        preference: agentPreference,
      },
      {
        normalizationSchema: agentPreferencesEntity,
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: true,
              content: 'Preferences changes saved',
            },
            failed: {
              content: 'Preferences changes failed',
              onClickAction: () =>
                dispatch(doUpdatePreferences(agentPreference, options)),
            },
          },
        },
        ...options,
      }
    )
  )
}
